<template>
  <div class="my-orders-page">
    <!-- 顶部导航栏 -->
    <div class="nav-bar">
      <span class="nav-back" @click="$router.push('/mine')">
        <i class="el-icon-arrow-left"></i>
      </span>
      <span class="nav-title">我的订单</span>
    </div>

    <!-- 状态筛选标签 -->
    <div class="tab-bar">
      <div
        v-for="tab in tabs"
        :key="tab.value"
        class="tab-item"
        :class="{ active: activeTab === tab.value }"
        @click="switchTab(tab.value)"
      >
        {{ tab.label }}
      </div>
    </div>

    <!-- 订单列表 -->
    <div class="order-list">
      <!-- 加载骨架 -->
      <div v-if="loading" class="skeleton-wrap">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-line w40"></div>
          <div class="skeleton-line w50"></div>
          <div class="skeleton-line w50"></div>
          <div class="skeleton-line w70"></div>
        </div>
      </div>

      <!-- 有数据 -->
      <template v-else-if="filteredOrders.length">
        <div
          v-for="order in filteredOrders"
          :key="order.id"
          class="order-card"
          @click="goDetail(order)"
        >
          <!-- 时间 + 状态 -->
          <div class="card-time-row">
            <span class="card-time">
              <i class="el-icon-time"></i>
              {{ order.add_time }}
            </span>
            <span class="card-status" :class="statusClass(order.daijia_server_status)">{{ order.daijiaServerStatusText }}</span>
          </div>

          <!-- 路线信息 -->
          <div class="card-route">
            <div class="route-point route-start">
              <i class="dot dot-blue"></i>
              <span>{{ order.start_city_name }}</span>
            </div>
            <!-- <div class="route-line"></div> -->
            <div class="route-point route-end">
              <i class="dot dot-orange"></i>
              <span>{{ order.end_city_name }}</span>
            </div>
          </div>

          <!-- 底部标签 + 交车时间 -->
          <div class="card-footer">
            <span class="order-tag">{{ order.channelText }}</span>
            <span class="deadline">规定交车时间{{ order.estimate_start_time }}</span>
          </div>
        </div>
      </template>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <svg class="empty-icon" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="30" y="20" width="50" height="65" rx="4" stroke="#999" stroke-width="2.5"/>
          <line x1="40" y1="35" x2="70" y2="35" stroke="#999" stroke-width="2" stroke-linecap="round"/>
          <line x1="40" y1="45" x2="70" y2="45" stroke="#999" stroke-width="2" stroke-linecap="round"/>
          <line x1="40" y1="55" x2="60" y2="55" stroke="#999" stroke-width="2" stroke-linecap="round"/>
          <circle cx="82" cy="78" r="18" stroke="#999" stroke-width="2.5"/>
          <line x1="95" y1="91" x2="105" y2="101" stroke="#999" stroke-width="3" stroke-linecap="round"/>
          <path d="M75 78 L79 82 L89 72" stroke="#4A90E2" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p class="empty-text">暂无相关订单</p>
      </div>

      <!-- 加载更多 -->
      <div v-if="filteredOrders.length && hasMore" class="load-more" @click="loadMore">
        <span v-if="!loadingMore">加载更多</span>
        <span v-else>加载中...</span>
      </div>
      <div v-if="filteredOrders.length && !hasMore" class="no-more">没有更多了</div>
    </div>
  </div>
</template>

<script>
import { getMyOrders } from '@/api/order'

export default {
  name: 'MyOrders',
  data() {
    const query = this.$route.query || {}
    let initTab = -1
    if (query.status !== undefined && query.status !== 'all') {
      initTab = Number(query.status)
    }
    return {
      loading: false,
      loadingMore: false,
      page: 1,
      size: 20,
      hasMore: true,
      activeTab: initTab,
      tabs: [
        { label: '全部', value: -1 },
        { label: '未接单', value: 0 },
        { label: '已接单', value: 1 },
        { label: '服务中', value: 2 },
        { label: '已完成', value: 3 }
      ],
      orders: []
    }
  },
  computed: {
    filteredOrders() {
      return this.orders
    }
  },
  created() {
    this.fetchOrders()
  },
  methods: {
    async fetchOrders() {
      this.loading = true
      this.page = 1
      this.hasMore = true
      try {
        const params = { page: this.page, size: this.size }
        if (this.activeTab !== -1) {
          params.status = this.activeTab
        }
        const res = await getMyOrders(params)
        const list = res.data && res.data.list ? res.data.list : []
        this.orders = list
        this.hasMore = list.length >= this.size
      } catch (err) {
        this.orders = []
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      if (this.loadingMore) return
      this.loadingMore = true
      this.page++
      try {
        const params = { page: this.page, size: this.size }
        if (this.activeTab !== -1) {
          params.status = this.activeTab
        }
        const res = await getMyOrders(params)
        const list = res.data && res.data.list ? res.data.list : []
        this.orders = this.orders.concat(list)
        this.hasMore = list.length >= this.size
      } catch (err) {
        this.page--
        this.$message.error(err.message || '加载更多失败')
      } finally {
        this.loadingMore = false
      }
    },
    switchTab(val) {
      this.activeTab = val
      this.fetchOrders()
    },
    statusClass(status) {
      const map = {
        0: 'status-teal',
        1: 'status-teal',
        2: 'status-orange',
        3: 'status-gray'
      }
      return map[status] || 'status-gray'
    },
    goDetail(order) {
      this.$router.push({
        path: '/order-detail',
        query: { id: order.id }
      })
    }
  }
}
</script>

<style scoped>
.my-orders-page {
  min-height: 100vh;
  background: #f5f5f5;
}

/* 顶部导航栏 */
.nav-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  background: #fff;
  position: relative;
}

.nav-back {
  position: absolute;
  left: 12px;
  font-size: 22px;
  color: #333;
  cursor: pointer;
  padding: 4px;
}

.nav-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

/* 状态筛选标签 */
.tab-bar {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.tab-item.active {
  color: #4a90e2;
  font-weight: 600;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  border-radius: 2px;
  background: #4a90e2;
}

/* 订单列表 */
.order-list {
  padding: 12px;
}

.order-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
}

/* 时间行 */
.card-time-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.card-time {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #999;
}

.card-time i {
  margin-right: 4px;
}

/* 状态标签颜色 */
.card-status {
  font-size: 13px;
  font-weight: 500;
}

.status-gray { color: #8c8c8c; }
.status-orange { color: #fa8c16; }
.status-teal { color: #13c2c2; }
.status-red { color: #f5222d; }

/* 路线信息 */
.card-route {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}

.route-point {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

.route-line {
  width: 1px;
  height: 8px;
  background: #e0e0e0;
  margin-left: 3.5px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}

.dot-blue { background: #4a90e2; }
.dot-orange { background: #ff9500; }

/* 底部标签 + 交车时间 */
.card-footer {
  display: flex;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
}

.order-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #fff7e6;
  color: #fa8c16;
  font-size: 11px;
  border-radius: 4px;
  margin-right: 8px;
  white-space: nowrap;
}

.deadline {
  font-size: 12px;
  color: #999;
}

/* 骨架屏 */
.skeleton-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
}

.skeleton-line {
  height: 14px;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: skeleton-pulse 1.2s ease-in-out infinite;
}

.skeleton-line:last-child { margin-bottom: 0; }

.w40 { width: 40%; }
.w50 { width: 50%; }
.w70 { width: 70%; }

@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 0;
}

.empty-icon {
  width: 120px;
  height: 120px;
}

.empty-text {
  margin-top: 16px;
  font-size: 15px;
  color: #999;
}

/* 加载更多 */
.load-more {
  text-align: center;
  padding: 14px;
  font-size: 14px;
  color: #4a90e2;
  cursor: pointer;
}

.no-more {
  text-align: center;
  padding: 14px;
  font-size: 13px;
  color: #ccc;
}
</style>
